import PropTypes from 'prop-types'
import {
  Flex,
  Text,
  Button,
  ButtonGroup,
  useDisclosure,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  Box,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Tag,
  TagLabel,
  TagLeftIcon,
} from '@chakra-ui/react'
import {CheckCircleIcon} from '@chakra-ui/icons'
import {useTranslation} from 'react-i18next'
import {useState, useMemo, useCallback, useRef} from 'react'
import {toast} from 'react-toastify'

import {useRails} from '~/react/contexts/rails'
import {ToastMessage} from '~/react/components/Toast'
import {CheckIcon, CrossIcon, ArrowDownIcon, LockIcon} from '~/react/icons'

import VirginBusinessConnectionDrawer from './VirginBusinessConnectionDrawer'
import QantasBusinessConnectionDrawer from './QantasBusinessConnectionDrawer'
import RedeemPointsModal from '../../components/RedeemPointsModal'
import RedeemPointsStatusModal from '../../components/RedeemPointsStatusModal'
import {disconnectMembership} from '../../api'
import {getButtonText, getButtonActions} from '../../utils/partnerUtils'
import useRedemptionPartnerLogo from '../../hooks/useRedemptionPartnerLogo'

const CardStatusTag = ({connected, setupRequired, handleDisconnectClick}) => {
  const {t} = useTranslation('points')
  const stopPropagation = (e) => e.stopPropagation()

  if (setupRequired) {
    return (
      <Box position="absolute" top="4" right="4" zIndex="1">
        <Tag variant="status" colorScheme="info">
          <TagLeftIcon
            boxSize="12px"
            mr="1"
            color="mld.neutral.800"
            as={LockIcon}
          />
          <TagLabel color="mld.neutral.800">
            {t('points:redemptionPartnerScreen.statusTag', {
              context: 'setupRequired',
            })}
          </TagLabel>
        </Tag>
      </Box>
    )
  }

  if (connected) {
    return (
      <Box position="absolute" top="4" right="4" zIndex="1">
        <Tag variant="status" colorScheme="success">
          <TagLeftIcon
            boxSize="12px"
            mr="1"
            color="mld.success.700"
            as={CheckCircleIcon}
          />
          <TagLabel>
            {t('points:redemptionPartnerScreen.statusTag', {
              context: 'connected',
            })}
          </TagLabel>
        </Tag>
        <Menu>
          <MenuButton
            as={IconButton}
            marginLeft="2px"
            size="xs"
            backgroundColor="mld.neutral.200"
            icon={<ArrowDownIcon />}
            onClick={stopPropagation}
          />
          <MenuList>
            <MenuItem onClick={handleDisconnectClick}>
              {t('points:redemptionPartnerScreen.disconnect')}
            </MenuItem>
          </MenuList>
        </Menu>
      </Box>
    )
  }
  return null
}

CardStatusTag.propTypes = {
  connected: PropTypes.bool.isRequired,
  setupRequired: PropTypes.bool.isRequired,
  handleDisconnectClick: PropTypes.func.isRequired,
}

const RedemptionPartnerCard = ({
  currentSupplier,
  partner,
  onConnectionSuccess,
  onRedeemSuccess,
  setupRequired,
}) => {
  const {t} = useTranslation('points')
  const {authenticityToken} = useRails()

  const {isOpen, onOpen, onClose} = useDisclosure()
  const [modalMode, setModalMode] = useState('closed')
  const [redeemData, setRedeemData] = useState(null)

  const PartnerLogo = useRedemptionPartnerLogo({
    redemptionPartnerKey: partner.key,
  })

  const {
    isOpen: isDisconnectConfirmOpen,
    onOpen: onDisconnectConfirmOpen,
    onClose: onDisconnectConfirmClose,
  } = useDisclosure()

  const cancelRef = useRef()

  const buttonText = useMemo(() => getButtonText(partner, t), [partner, t])

  const {
    shouldShowRedeemButton,
    redeemButtonDisabled,
    isConnectButtonDisabled,
  } = useMemo(() => getButtonActions(partner), [partner])

  const handleRedeemClick = useCallback(() => {
    setModalMode('redeem')
  }, [])

  const handleRedeemSuccess = useCallback(
    (data) => {
      setRedeemData(data)
      onRedeemSuccess()
      setModalMode('processing')
    },
    [onRedeemSuccess],
  )

  const handleRedeemError = useCallback((data) => {
    setRedeemData(data)
    setModalMode('failed')
  }, [])

  const handleCloseModals = useCallback(() => {
    setModalMode('closed')
  }, [])

  const handleConnectClick = useCallback(() => {
    onOpen()
  }, [onOpen])

  const handleDisconnectClick = useCallback(() => {
    onDisconnectConfirmOpen()
  }, [onDisconnectConfirmOpen])

  const onSuccessToast = useCallback((message) => {
    toast.dark(
      <ToastMessage>
        <CheckIcon mr={1} color="mld.success.700" />
        {message}
      </ToastMessage>,
    )
  }, [])

  const onErrorToast = useCallback((message) => {
    toast.dark(
      <ToastMessage>
        <CrossIcon mr={1} color="mld.alert.700" />
        {message}
      </ToastMessage>,
    )
  }, [])

  const handleDisconnectConfirm = useCallback(async () => {
    try {
      if (!partner.membership?.id) {
        onErrorToast(
          t('points:redemptionPartnerScreen.disconnectError.noMembership'),
        )
        return
      }

      await disconnectMembership({
        supplierId: currentSupplier.id,
        authenticityToken,
        partnerKey: partner.key,
      })

      onDisconnectConfirmClose()
      onConnectionSuccess()
      onSuccessToast(t('points:redemptionPartnerScreen.disconnectSuccess'))
    } catch {
      onErrorToast(t('points:redemptionPartnerScreen.disconnectError.failed'))
    }
  }, [
    currentSupplier.id,
    partner.key,
    authenticityToken,
    onDisconnectConfirmClose,
    onConnectionSuccess,
    onSuccessToast,
    onErrorToast,
    partner.membership?.id,
    t,
  ])

  return (
    <>
      <Box data-testid={partner.key}>
        <Box width={{base: 'auto', md: '335px'}} position="relative">
          <Box backgroundColor="mld.neutral.50" borderRadius="lg">
            <CardStatusTag
              setupRequired={setupRequired}
              connected={
                !!(partner.membership && partner.membership.status === 'active')
              }
              handleDisconnectClick={handleDisconnectClick}
            />

            <Flex
              height="245px"
              justifyContent="center"
              alignContent="center"
              flexWrap="wrap"
            >
              <PartnerLogo width="242px" />
            </Flex>
          </Box>
          <Flex flexDirection="column" gap="sm" py="sm">
            <Text textStyle="body-copy-medium">{partner.name}</Text>
            <Text textStyle="body-small" color="mld.neutral.700">
              {t(`points:redemptionPartnerCard.partners.${partner.key}.about`)}
            </Text>
            {!setupRequired && (
              <ButtonGroup spacing="2">
                {shouldShowRedeemButton ? (
                  <Button
                    variant="secondary"
                    isDisabled={redeemButtonDisabled}
                    onClick={handleRedeemClick}
                  >
                    {t('points:redemptionPartnerScreen.redeem')}
                  </Button>
                ) : (
                  <Button
                    variant="secondary"
                    isDisabled={isConnectButtonDisabled}
                    onClick={handleConnectClick}
                  >
                    {buttonText}
                  </Button>
                )}
              </ButtonGroup>
            )}
          </Flex>
        </Box>
      </Box>
      {partner.status === 'enabled' && partner.key === 'VABF' && (
        <VirginBusinessConnectionDrawer
          isOpen={isOpen}
          onClose={onClose}
          partner={partner}
          onSuccess={onConnectionSuccess}
        />
      )}
      {partner.status === 'enabled' && partner.key === 'QBR' && (
        <QantasBusinessConnectionDrawer
          isOpen={isOpen}
          onClose={onClose}
          partner={partner}
          onSuccess={onConnectionSuccess}
        />
      )}
      {modalMode === 'redeem' && (
        <RedeemPointsModal
          key="redeem-modal"
          isOpen
          onClose={handleCloseModals}
          onSuccess={handleRedeemSuccess}
          onError={handleRedeemError}
          mldPointRateFactor={partner.mldPointRateFactor}
          partnerPointRateFactor={partner.externalPointRateFactor}
          partnerPointsMaximum={partner.partnerPointsMaximum}
          partnerId={partner.id}
          partnerKey={partner.key}
          supplierId={currentSupplier.id}
          membership={partner.membership}
        />
      )}
      {redeemData && ['processing', 'failed'].includes(modalMode) && (
        <RedeemPointsStatusModal
          isOpen
          onClose={handleCloseModals}
          redemptionStatus={modalMode}
          partnerKey={redeemData.partnerKey}
          partnerPoints={redeemData.partnerPoints}
          convertAmount={redeemData.convertAmount}
          currentBalance={redeemData.currentBalance}
        />
      )}

      <AlertDialog
        isOpen={isDisconnectConfirmOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDisconnectConfirmClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t('points:redemptionPartnerScreen.disconnectConfirmation.title')}
            </AlertDialogHeader>

            <AlertDialogBody>
              {t(
                'points:redemptionPartnerScreen.disconnectConfirmation.message',
                {partnerName: partner.name},
              )}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button
                variant="primary"
                onClick={handleDisconnectConfirm}
                mr="sm"
              >
                {t(
                  'points:redemptionPartnerScreen.disconnectConfirmation.confirm',
                )}
              </Button>
              <Button
                variant="tertiary"
                ref={cancelRef}
                onClick={onDisconnectConfirmClose}
              >
                {t(
                  'points:redemptionPartnerScreen.disconnectConfirmation.cancel',
                )}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  )
}

RedemptionPartnerCard.propTypes = {
  currentSupplier: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
  partner: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    key: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    membership: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      status: PropTypes.string,
      pointsAccount: PropTypes.shape({
        balance: PropTypes.number,
      }),
    }),
    externalPointRateFactor: PropTypes.number.isRequired,
    mldPointRateFactor: PropTypes.number.isRequired,
    partnerPointsMaximum: PropTypes.number,
    status: PropTypes.oneOf(['enabled', 'coming_soon', 'paused']).isRequired,
  }).isRequired,
  onConnectionSuccess: PropTypes.func.isRequired,
  onRedeemSuccess: PropTypes.func.isRequired,
  setupRequired: PropTypes.bool.isRequired,
}

export default RedemptionPartnerCard
